import { Button, Checkbox, Col, ConfigProvider, DatePicker, Form, Input, InputNumber, Row, Segmented, Select, Space, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react'
import { LOGGED_USER } from '../../../../variables/constants';
import PreviewPrimes from './PreviewPrimes';
import frFR from 'antd/locale/fr_FR';
import dayjs from 'dayjs';
import { estBureauDirect, peutProduirePourCourtier, rightsService } from '../../../../services/rightsService';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import { useParams } from 'react-router-dom';

const GarantiesForm = ({ categorie, selectedCategorie, fractionnements, preview, fractionnementSelect, setFractionnementSelect, dateEffet, setDateEffet, sameYear, loadingPreview, tauxCommission, setTauxCommission, agencyProduction, setAgencyProduction, setIntermediaire, showPreview, canEditAccessoires, accessoiresEdites, setAccessoiresEdites, bonusMalusEdite, setBonusMalusEdite, membrePersonnel, setMembrePersonnel, canMarkPersonnel, contratEtat, setContratEtat }) => {
    // Bonus/Malus transféré : non applicable aux contrats VT (montants forfaitaires)
    const isVt = (selectedCategorie?.codeCategorie || categorie?.codeCategorie || "").startsWith("VT");
    // L'édition du taux de bonus/malus est soumise à habilitation (HCO080), hors VT.
    const canEditBonus = rightsService("HCO080") && !isVt;
    // Souscription pour l'État (HCO106) : impression sans encaissement, paiement État sous 6 mois.
    const canMarkEtat = rightsService("HCO106");
    // Sens du bonus/malus (le signe est géré pour l'utilisateur via le switch ci-dessous)
    const [bonusMalusSens, setBonusMalusSens] = useState('BONUS');
    const bonusMalusAbs = Math.abs(Number(bonusMalusEdite) || 0);
    // Plafond du bonus (%), PARAMÉTRABLE côté serveur (table Parametre). 30 % par défaut
    // tant que la valeur n'est pas chargée / si non paramétrée.
    const [BONUS_MAX, setBonusMax] = useState(30);
    // Plafond du bonus « membre du personnel » (séparé, défaut 50 %).
    const [BONUS_MAX_PERSONNEL, setBonusMaxPersonnel] = useState(50);
    useEffect(() => {
        axiosInstance.get('/api/parametre/plafond-bonus')
            .then((res) => {
                const v = Number(res.data);
                if (!Number.isNaN(v) && v > 0) setBonusMax(v);
            })
            .catch(() => { /* on garde le défaut 35 */ });
        axiosInstance.get('/api/parametre/plafond-bonus-personnel')
            .then((res) => {
                const v = Number(res.data);
                if (!Number.isNaN(v) && v > 0) setBonusMaxPersonnel(v);
            })
            .catch(() => { /* défaut 50 conservé */ });
    }, []);
    // Plafond effectif : max personnel si « membre du personnel », sinon plafond bonus standard.
    const effectiveBonusMax = membrePersonnel ? BONUS_MAX_PERSONNEL : BONUS_MAX;
    // Applique le sens (Bonus = +, Malus = −) à la magnitude saisie en %.
    const applyBonusSens = (sens, magnitude) => {
        setBonusMalusSens(sens);
        let a = Math.abs(Number(magnitude) || 0);
        if (sens === 'BONUS' && a > effectiveBonusMax) a = effectiveBonusMax; // plafond (perso si membre du personnel)
        setBonusMalusEdite && setBonusMalusEdite(a === 0 ? null : (sens === 'MALUS' ? -a : a));
    };
    // Coche « membre du personnel » : défaut = max personnel (bonus), décoche → réinitialise.
    const toggleMembrePersonnel = (checked) => {
        setMembrePersonnel && setMembrePersonnel(checked);
        if (checked) {
            setBonusMalusSens('BONUS');
            setBonusMalusEdite && setBonusMalusEdite(BONUS_MAX_PERSONNEL);
        } else {
            setBonusMalusEdite && setBonusMalusEdite(null);
        }
    };
    // Aligne le switch sur le signe de la valeur (utile si rechargée depuis un contrat existant).
    useEffect(() => {
        if (bonusMalusEdite != null) {
            setBonusMalusSens(Number(bonusMalusEdite) < 0 ? 'MALUS' : 'BONUS');
        }
    }, [bonusMalusEdite]);
    const [formGaranties] = Form.useForm();
    const [selectedCompany, setSelectedCompany] = useState(JSON.parse(sessionStorage.getItem("currentCompagnie"))?.code || "");
    const [selectedIntermediaire, setSelectedIntermediaire] = useState(null);
    const [tc, setTC] = useState(0);
    const [courtiers, setCourtiers] = useState([]); // à récupérer via API
    const [intermediaires, setIntermediaires] = useState([]); // à récupérer via API
    const [agences, setAgences] = useState([]); // à récupérer via API

    const param = useParams();

    // Siège (ou POOL admin sans agence) = peut modifier agence + taux.
    // Sinon (agence non-siège), les champs sont figés.
    const isSiegeOuPool = !LOGGED_USER?.agence || LOGGED_USER?.agence?.typeAgence === "SIEGE";
    // Siège, bureau direct ou Pool : « Vous produisez pour » est ouvert (choix d'un courtier).
    // Le bureau direct garde le taux figé et, côté « Agence », sa seule agence (sauf HLA068).
    const peutCiblerCourtier = peutProduirePourCourtier();
    const bureauDirect = estBureauDirect();

    // Agence ouverte à la production : restrictionProduction ≠ true. Deux agences DIFFÉRENTES
    // peuvent partager un même code (SAVANA LOME active vs KARA restreinte, code 2027) — ne
    // garder que les non restreintes lève l'ambiguïté de code. `!== true` conserve les false
    // ET les null (agences legacy sans le champ).
    const productionActive = (a) => a?.restrictionProduction !== true;

    const getSimpleAgencies = () => {
        let agencies = [];
        agencies = agences.filter((a) => a.typeAgence !== "COURTIER" && productionActive(a));
        return agencies;
    };

    // Comparateur alphabétique croissant (français, insensible casse/accents).
    // String(...) protège contre les libellés non-chaîne (ex. nom/code numérique),
    // car localeCompare n'existe que sur String.
    const sortByLabelAsc = (a, b) =>
        String(a?.label ?? "").localeCompare(String(b?.label ?? ""), "fr", { sensitivity: "base" });

    // Options du select « Vous produisez pour » : deux groupes (Agence / Courtier),
    // chacun trié par ordre alphabétique croissant sur le nom.
    const productionOptions = useMemo(() => {
        const agenceOpts = (getSimpleAgencies() || [])
            .map((a) => rightsService("HLA068")
                ? { label: a.nom, value: a.codeAgence, key: `ag_${a.codeId}` }
                : (LOGGED_USER?.agence?.codeAgence === a.codeAgence
                    ? { label: a.nom, value: a.codeAgence, key: `agu_${a.codeId}` }
                    : null))
            .filter(Boolean)
            .sort(sortByLabelAsc);

        const courtierOpts = (courtiers || [])
            .filter(productionActive)
            .map((c) => ({ label: c.nom, value: c.codeAgence, key: `co_${c.codeId}` }))
            .sort(sortByLabelAsc);

        return [
            { label: <span>Agence</span>, title: 'agence', options: agenceOpts },
            { label: <span>Courtier</span>, title: 'courtier', options: courtierOpts },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agences, courtiers]);

    // Options de la liste des intermédiaires :
    //  - triées par ordre alphabétique croissant sur le nom complet ;
    //  - libellé de repli sur le code si le nom est vide (évite les lignes vides
    //    et permet aussi la recherche par code).
    const intermediaireOptions = useMemo(() => {
        return [...(intermediaires || [])]
            .map((inter) => {
                const fullName = `${inter?.personne?.nom ?? ""} ${inter?.personne?.prenom ?? ""}`.trim();
                const label = fullName || inter?.codeIntermediaire || "";
                return {
                    label,
                    value: inter.codeIntermediaire,
                    key: inter.codeIntermediaire,
                };
            })
            .sort(sortByLabelAsc);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intermediaires]);

    const handleCourtierChange = (e) => {
        setSelectedCompany(e);
    };

    const handleAgenceChange = (value) => {
        // Préférer l'agence NON restreinte quand un code est partagé (SAVANA LOME vs KARA).
        let ag = agences.find((el) => el.codeAgence === value && productionActive(el))
            || agences.find((el) => el.codeAgence === value);
        if (ag && ag.typeAgence !== "COURTIER") {
            getIntermediairesByAgency(value);
        } else {
            setIntermediaires([]);
            ag = courtiers.find((el) => el.codeAgence === value && productionActive(el))
                || courtiers.find((el) => el.codeAgence === value);
        }
        // Fetch commission rate for any agence type (courtier or not)
        getTauxCommissionByCourtierAndCodeCategorie(
            value,
            LOGGED_USER.profil.organisme.code
        );
        setAgencyProduction(ag);
    };

    const getIntermediairesByAgency = (codeAgence) => {
        axiosInstance
            .get(`/api/intermediaire/compagnie/${codeAgence}`)
            .then((res) => {
                setIntermediaires(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getTauxCommissionByCourtierAndCodeCategorie = (
        codeAgence,
        codeCompagnie
    ) => {
        axiosInstance
            .get(
                `/api/agence/taux-commission/${codeAgence}/${selectedCategorie}/${codeCompagnie}`
            )
            .then((res) => {
                console.log("taux-commission-fetched: ", res.data);
                if (!res.data) {
                    toast(
                        "Taux de commission non encore défini pour cette catégorie pour le courtier"
                    );
                }
                res.data && setTauxCommission(res.data);
                res.data && setTC(res.data);
                formGaranties.setFieldsValue({ tauxCommission: res.data });
            })
            .catch((err) => {
                console.log(err);
                toast(
                    "Taux de commission non encore défini pour cette catégorie pour le courtier"
                );
            });
    };

    const getTauxCommissionByIntermediaireAndCodeCategorie = (
        codeIntermediaire, codeCompagnie
    ) => {
        axiosInstance
            .get(`/api/intermediaire/taux-commission/${codeIntermediaire}/${categorie}/${codeCompagnie}`)
            .then((res) => {
                console.log("taux-commission-fetched: ", res.data);
                res.data && setTauxCommission(res.data);
                res.data && setTC(res.data);
                formGaranties.setFieldsValue({ tauxCommission: res.data });
            })
            .catch((err) => {
                console.log(err);
                toast(
                    "Taux de commission non encore défini pour cette catégorie pour ce courtier"
                );
            });
    };

    const handleIntermediaireChange = (e) => {
        getTauxCommissionByIntermediaireAndCodeCategorie(e.target.value, LOGGED_USER.profil.organisme.code);
        setSelectedIntermediaire(e.target.value);
    };

    // Antidatage : avec le droit HCO081, on autorise une date d'effet dans le passé.
    const canAntidate = rightsService("HCO081");
    const disabledDate = (current) => {
        if (canAntidate) return false;
        return current && current < dayjs().startOf('day');
    };

    const getDateEcheance = (date) => {
        setDateEffet(date);
    };

    useEffect(() => {
        axiosInstance
            .get(
                `/api/agence/organisme/code/${LOGGED_USER?.profil?.organisme?.code}`
            )
            .then((res) => {
                console.log("Agences: ", res.data);
                setAgences(res.data);
            })
            .catch((err) => {
                console.log(err);
            });

        // Récupération des courtiers
        axiosInstance
            .get(`/api/agence/all/courtiers`)
            .then((res) => {
                console.log("Courtiers: ", res.data);
                setCourtiers(res.data);
            })
            .catch((err) => {
                console.log(err);
            });

        axiosInstance
            .get(
                `/api/intermediaire/compagnie/actifs/${LOGGED_USER?.profil?.organisme?.code}`
            )
            .then((res) => {
                setIntermediaires(res.data);
            })
            .catch((err) => {
                console.log(err);
            });

        if (LOGGED_USER?.agence?.typeAgence === "COURTIER" && selectedCategorie) {
            getTauxCommissionByCourtierAndCodeCategorie(
                LOGGED_USER.agence?.codeAgence,
                JSON.parse(sessionStorage.getItem("currentCompagnie"))?.code
            );
            console.log("selectedCategorie: ", JSON.parse(sessionStorage.getItem("currentCompagnie"))?.code);
            setSelectedCompany(
                JSON.parse(sessionStorage.getItem("currentCompagnie"))?.code ||
                ""
            );
        }

        // Agence NON siège (ni courtier) : on fige sur sa propre agence et on
        // charge automatiquement son taux de commission pour la catégorie
        // (le taux n'est appliqué que s'il est > 0, cf. getTaux...).
        // Bureau direct : sa propre agence n'est que le choix par défaut ; on garde le courtier déjà
        // retenu (proposition rééditée, retour sur cet onglet).
        if (!isSiegeOuPool
            && LOGGED_USER?.agence?.typeAgence !== "COURTIER"
            && LOGGED_USER?.agence?.codeAgence
            && selectedCategorie) {
            const cible = bureauDirect && agencyProduction?.typeAgence === "COURTIER" && agencyProduction?.codeAgence
                ? agencyProduction
                : LOGGED_USER.agence;
            setAgencyProduction(cible);
            // Pré-remplit la valeur du formulaire (le Select figé ne déclenche pas onChange)
            formGaranties.setFieldsValue({ agence: cible.codeAgence });
            getTauxCommissionByCourtierAndCodeCategorie(
                cible.codeAgence,
                LOGGED_USER.profil?.organisme?.code
            );
        }
    }, [selectedCategorie]);

    useEffect(() => {
        formGaranties.setFieldValue("fractionnement", fractionnementSelect);
    }, [fractionnementSelect]);


    return (
        <div className="row g-6">
            <div className="col-12">
                <Form form={formGaranties} layout="vertical"
                    initialValues={{
                        fractionnement: fractionnementSelect || "",
                        dateEffet: dateEffet || dayjs(),
                        company: JSON.parse(sessionStorage.getItem("currentCompagnie"))?.code || LOGGED_USER?.profil?.organisme?.code || "",
                        tauxCommission: tauxCommission || 0,
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Durée"
                                name="fractionnement"
                            >
                                <Select
                                    placeholder="1 an"
                                    onChange={(val) => {
                                        // conserve l'état existant et met à jour preview
                                        setFractionnementSelect(val || "");
                                        formGaranties.setFieldsValue({ fractionnement: val });
                                    }}
                                    allowClear
                                >
                                    <Select.Option value="">1 an</Select.Option>
                                    {!sameYear ? fractionnements && fractionnements?.map((fra) => fra.nombre !== 1 ? (
                                        <Select.Option key={fra.fractionnementId} value={fra.fractionnementId}>
                                            {fra.moisMin} {fra.periode || "Mois"}
                                        </Select.Option>
                                    ) : null) : null}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            {LOGGED_USER?.agence?.typeAgence === "COURTIER" ? (
                                <Form.Item label="Compagnie (Courtier)" name="company" rules={[{ required: true, message: "Sélectionnez la compagnie" }]}>
                                    <Select
                                        placeholder="Sélectionnez la compagnie"
                                        // value={selectedCompany || ""}
                                        onChange={(val) => {
                                            setSelectedCompany(val);
                                            handleCourtierChange({ target: { value: val } });
                                        }}
                                        disabled={LOGGED_USER?.agence?.typeAgence === "COURTIER"}
                                    >
                                        {LOGGED_USER?.agence?.validAgrements?.map((agr) => (
                                            <Select.Option key={agr.compagnie.code} value={agr.compagnie.code}>
                                                {agr?.compagnie?.raisonSociale}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : (
                                <Form.Item label="Vous produisez pour" name="agence" rules={[{ required: true, message: "Sélectionnez l'agence / courtier" }]}>
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        filterOption={(input, option) => {
                                            const label = typeof option?.label === "string" ? option.label : "";
                                            return label.toLowerCase().includes((input ?? "").toLowerCase());
                                        }}
                                        filterSort={sortByLabelAsc}
                                        placeholder="Sélectionnez l'agence / courtier"
                                        onChange={(val) => handleAgenceChange(val)}
                                        disabled={!peutCiblerCourtier}
                                        defaultValue={param?.contratId && agencyProduction ? agencyProduction?.nom : LOGGED_USER?.agence?.nom}
                                        options={productionOptions}
                                    />
                                </Form.Item>
                            )}
                        </Col>

                        {intermediaires && intermediaires?.length ? <Col span={12}>
                            <Form.Item label="Intermédiaire" name="intermediaire">
                                <Select
                                    showSearch
                                    allowClear
                                    optionFilterProp="label"
                                    filterOption={(input, option) =>
                                        (option?.label ?? "").toLowerCase().includes((input ?? "").toLowerCase())
                                    }
                                    filterSort={(a, b) =>
                                        (a?.label ?? "").localeCompare((b?.label ?? ""), "fr", { sensitivity: "base" })
                                    }
                                    placeholder="Sélectionnez l'intermédiaire (optionnel)"
                                    value={selectedIntermediaire || undefined}
                                    options={intermediaireOptions}
                                    onChange={(val) => {
                                        setSelectedIntermediaire(val || null);
                                        handleIntermediaireChange({ target: { value: val } });
                                        const fullObj = intermediaires.find((i) => i.codeIntermediaire === val);
                                        if (setIntermediaire) setIntermediaire(fullObj || null);
                                    }}
                                />
                            </Form.Item>
                        </Col> : null}

                        {intermediaires?.length || LOGGED_USER?.agence?.typeAgence === "COURTIER" || agencyProduction ?
                            <Col span={12}>
                                <Form.Item label="Taux de commission" name="tauxCommission">
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        onChange={(val) => { setTauxCommission(Number(val || 0)); setTC(Number(val || 0)) }}
                                        min={0}
                                        max={100}
                                        disabled={
                                            !isSiegeOuPool ||
                                            LOGGED_USER?.agence?.typeAgence === "COURTIER" ||
                                            agencyProduction?.typeAgence === "COURTIER"
                                        }
                                    />
                                </Form.Item>
                            </Col> : null}

                        <Col span={12}>
                            <Form.Item label="Accessoires"
                                extra={canEditAccessoires
                                    ? `Par défaut : ${displayNumbers(preview?.accessoiresDefaut != null ? preview?.accessoiresDefaut : preview?.accessoires) || 0} FCFA`
                                    : null}>
                                {canEditAccessoires ? (
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        value={accessoiresEdites != null ? accessoiresEdites : preview?.accessoires}
                                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                        parser={(v) => v.replace(/\s/g, "")}
                                        onChange={(v) => setAccessoiresEdites && setAccessoiresEdites(v)}
                                        onPressEnter={() => showPreview && showPreview()}
                                    />
                                ) : (
                                    <Input value={displayNumbers(preview?.accessoires) || 0} disabled />
                                )}
                            </Form.Item>
                        </Col>

                        {/* Membre du personnel (HCO090) — bonus pris au max personnel par défaut */}
                        {canMarkPersonnel && !isVt && setMembrePersonnel && (
                            <Col span={12}>
                                <Form.Item label="Membre du personnel" extra="Applique le bonus personnel (max paramétré par le Pool) sur tous les engins.">
                                    <Checkbox
                                        checked={!!membrePersonnel}
                                        onChange={(e) => { toggleMembrePersonnel(e.target.checked); showPreview && showPreview(); }}
                                    >
                                        Contrat appartenant à un membre du personnel
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Souscription pour l'État (HCO106) — impression CP/attestations sans encaissement */}
                        {canMarkEtat && setContratEtat && (
                            <Col span={12}>
                                <Form.Item label="Souscription pour l'État" extra="Impression sans encaissement ; l'État paie sous 6 mois (à compter de l'impression).">
                                    <Checkbox
                                        checked={!!contratEtat}
                                        onChange={(e) => setContratEtat(e.target.checked)}
                                    >
                                        Contrat des engins de l'État
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Bonus / Malus transféré (hors VT) — HCO080, OU flux « membre du personnel » (HCO090) */}
                        {(canEditBonus || membrePersonnel) && setBonusMalusEdite && (
                            <Col span={12}>
                                <Form.Item
                                    label="Bonus / Malus transféré (%)"
                                    extra={membrePersonnel
                                        ? `Bonus « membre du personnel » : max ${BONUS_MAX_PERSONNEL} %, éditable à la baisse.`
                                        : "Engin venant d'une autre compagnie. Bonus = réduction, Malus = majoration. Entrée ou « Recalculer » pour appliquer."}
                                >
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Segmented
                                            value={bonusMalusSens}
                                            onChange={(sens) => applyBonusSens(sens, bonusMalusAbs)}
                                            options={[
                                                { label: 'Bonus', value: 'BONUS' },
                                                { label: 'Malus', value: 'MALUS' },
                                            ]}
                                        />
                                        <InputNumber
                                            style={{ flex: 1 }}
                                            min={0}
                                            max={bonusMalusSens === 'BONUS' ? effectiveBonusMax : 100}
                                            step={1}
                                            addonAfter="%"
                                            value={bonusMalusAbs}
                                            placeholder="0"
                                            onChange={(v) => applyBonusSens(bonusMalusSens, v)}
                                            onPressEnter={() => showPreview && showPreview()}
                                        />
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        )}

                        <Col span={12}>
                            <Form.Item
                                label="Date effet *"
                                name="dateEffet"
                                rules={[{ required: true, message: "La date d'effet est requise" }]}
                            >
                                <ConfigProvider locale={frFR}>
                                    <DatePicker
                                        showTime
                                        style={{ width: "100%" }}
                                        disabledDate={disabledDate}
                                        value={dateEffet || dayjs()}
                                        onChange={(val) => {
                                            // Effet FUTUR (date strictement après aujourd'hui) → heure calée à 00:00:00.
                                            // Effet du jour → on garde l'heure (calée à l'encaissement côté backend).
                                            const norm = (val && val.isAfter(dayjs(), "day")) ? val.startOf("day") : val;
                                            setDateEffet(norm);
                                            formGaranties.setFieldsValue({ dateEffet: norm });
                                            getDateEcheance(norm);
                                        }}
                                    />
                                </ConfigProvider>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <div className="d-flex justify-content-end mb-2">
                                <Button
                                    type="primary"
                                    icon={<ReloadOutlined />}
                                    loading={loadingPreview}
                                    onClick={() => showPreview && showPreview()}
                                >
                                    Recalculer l'aperçu
                                </Button>
                            </div>
                        </Col>

                        <Col span={24}>
                            <div className="container">
                                <Spin spinning={loadingPreview}>
                                    <PreviewPrimes preview={preview} />
                                </Spin>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </div>
        </div>
    )
}

export default GarantiesForm